import React, { useState, useRef, useEffect } from 'react';
import { toastService } from '../../services/toastService';

const AudioWidget = ({ value, onChange, label }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (value && value instanceof File) {
      const url = URL.createObjectURL(value);
      setAudioURL(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof value === 'string' && value.length > 0) {
      setAudioURL(value);
    }
  }, [value]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
        onChange(audioFile);
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toastService.error('Could not access microphone. Please try uploading a file instead.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('audio/')) {
        onChange(file);
      } else {
        toastService.error('Please select a valid audio file.');
      }
    }
  };

  const clearAudio = () => {
    onChange(null);
    setAudioURL(null);
    setRecordingTime(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3 p-4 border rounded-[6px] bg-white shadow-sm border-gray-200">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-gray-900">{label || 'Audio Recording'}</label>
        {isRecording && <span className="text-red-500 text-sm font-bold animate-pulse">Recording: {formatTime(recordingTime)}</span>}
      </div>

      {!audioURL && !isRecording && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={startRecording}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-[6px] hover:bg-blue-700 transition-colors duration-200"
          >
            Start Recording
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-[6px] hover:bg-gray-50 transition-colors duration-200"
          >
            Upload Audio
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="audio/*"
            onChange={handleFileUpload}
          />
        </div>
      )}

      {isRecording && (
        <button
          type="button"
          onClick={stopRecording}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-[6px] hover:bg-red-700 transition-colors duration-200"
        >
          Stop Recording
        </button>
      )}

      {audioURL && (
        <div className="flex flex-col gap-2">
          <audio controls src={audioURL} className="w-full mt-2" />
          <button 
           type="button" 
           onClick={clearAudio} 
           className="self-start text-sm px-3 py-1.5 font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-[4px] transition-colors"
          >
            Remove Audio
          </button>
        </div>
      )}
    </div>
  );
};

export default AudioWidget;
